/**
 * 极简 API 客户端
 * - 前端调用,不依赖任何 UI 框架
 * - 统一返回 ApiResponse<T>,失败时由调用方决定如何提示
 *
 * 重构版:增加 A 队 publish/search/verify/stats 与 B 队 plant/observation/
 * advice/careAction/timeline/report,旧 observe/reason/evaluate 仍保留
 */
import { apiUrl } from './api-base';
import type { ApiResponse, ApiErr } from './types';
import type {
  AnalyzePublishItemRequest,
  PublishItemRequest,
  PublishItemResult,
  SearchItemRequest,
  SearchItemResult,
  SearchFeedback,
  ClaimAnswerRequest,
  StatsReport,
  CreatePlantRequest,
  Plant,
  DailyObservationRequest,
  ObservationResult,
  ObservationContext,
  CareAdviceResult,
  CareActionRecord,
  CareActionRequest,
  TimelineResult,
  StageReportRequest,
  StageReportResult,
  StageReportListResult,
} from './schemas';
import type { LostItem } from './schemas';

function formatApiIssues(issues: unknown): string | null {
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const first = issues[0] as { path?: (string | number)[]; message?: string };
  const field = first.path?.join('.') || '输入';
  return `${field}: ${first.message ?? '格式不正确'}`;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly data?: unknown,
    public readonly issues?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 把后端返回的图片 key 拼成可用的 URL
 * 1) 如果后端已经返回了完整 imageUrl(配了 R2 公网域名),直接用
 * 2) 否则用 imageKey 走 Worker 的 /image 代理(无需 R2 公网域名)
 * 3) 都没有 → 返回 null(前端显示占位)
 */
export function resolveImageUrl(input: {
  imageUrl?: string | null;
  imageKey?: string | null;
  team: 'a' | 'b';
}): string | null {
  if (input.imageUrl) return input.imageUrl;
  if (!input.imageKey) return null;
  return apiUrl(`/api/${input.team}/image?key=${encodeURIComponent(input.imageKey)}`);
}

export async function apiFetch<T>(
  path: string,
  body: unknown,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    ...init,
  });
  const json = (await res.json().catch(() => ({}))) as ApiResponse<T> & { issues?: unknown };
  if (!json.ok) {
    const errJson = json as ApiErr;
    throw new ApiError(
      errJson.error,
      errJson.message ?? formatApiIssues(errJson.issues) ?? errJson.error,
      res.status,
      (json as { data?: unknown }).data,
      errJson.issues,
    );
  }
  return json.data;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path));
  const json = (await res.json().catch(() => ({}))) as ApiResponse<T>;
  if (!json.ok) {
    throw new ApiError(json.error, json.message ?? json.error, res.status);
  }
  return json.data;
}

// ============ A 队 · 小物归队(信任型自助领取) ============
export const TeamAApi = {
  publish: (body: PublishItemRequest) =>
    apiFetch<PublishItemResult>('/api/a/publish', body),
  analyzePublish: (body: AnalyzePublishItemRequest) =>
    apiFetch<PublishItemResult>('/api/a/publish/analyze', body),
  listItems: (params: { category?: string; status?: string; group?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.category) q.set('category', params.category);
    if (params.status) q.set('status', params.status);
    if (params.group) q.set('group', params.group);
    const qs = q.toString();
    return apiGet<{ items: LostItem[] }>(`/api/a/items${qs ? `?${qs}` : ''}`);
  },
  getItem: (id: string) => apiGet<LostItem>(`/api/a/items/${id}`),
  search: (body: SearchItemRequest) =>
    apiFetch<SearchItemResult>('/api/a/search', body),
  /** 自助领取:失主二次确认"我已取回" → 状态变 returned */
  pickup: (body: ClaimAnswerRequest) =>
    apiFetch<{ itemId: string; itemStatus: string }>('/api/a/pickup', body),
  /** 寻找反馈(没找到 / 不是我的),不改变 item 状态 */
  feedback: (body: SearchFeedback) =>
    apiFetch<{ ok: boolean }>('/api/a/feedback', body),
  stats: () => apiGet<StatsReport>('/api/a/stats'),
  // 演示数据(开发用,生产可禁用)
  seed: () => apiFetch<{ inserted: number }>('/api/a/seed', {}),
  reset: () => apiFetch<{ ok: boolean }>('/api/a/reset', {}),
};

// ============ B 队 · 叶子在说话 ============
export const TeamBApi = {
  // 新链路(家庭植物养护)
  listPlants: () => apiGet<{ plants: Plant[] }>('/api/b/plants'),
  createPlant: (body: CreatePlantRequest) =>
    apiFetch<Plant>('/api/b/plants', body),
  getPlant: (id: string) => apiGet<Plant>(`/api/b/plants/${id}`),
  /** AI 植物种类识别 */
  identifyPlant: (body: { imageBase64: string }) =>
    apiFetch<{
      species: string;
      confidence: 'high' | 'medium' | 'low';
      growthStage: '幼苗' | '成株' | '老株';
      healthStatus: '健康' | '亚健康' | '病态';
      features: {
        leafColor: string;
        leafShape: string;
        soilVisible: boolean;
        potSize: '小盆' | '中盆' | '大盆' | '无盆' | '不可见';
      };
    }>('/api/b/identify', body),
  /** AI 病虫害识别 */
  detectDisease: (body: { imageBase64: string }) =>
    apiFetch<{
      hasIssue: boolean;
      diseases: Array<{
        name: string;
        confidence: 'high' | 'medium' | 'low';
        location: string;
        severity: '轻微' | '中度' | '严重';
        treatment: string;
      }>;
      overallAssessment: string;
    }>('/api/b/disease', body),
  dailyObservation: (body: DailyObservationRequest) =>
    apiFetch<ObservationResult>('/api/b/observations', body),
  getObservation: (observationId: string) =>
    apiGet<ObservationContext>(`/api/b/observations/${encodeURIComponent(observationId)}`),
  /** 读取某条观察已保存的最新诊断(无则 null) */
  getSavedAdvice: (observationId: string) =>
    apiGet<CareAdviceResult | null>(
      `/api/b/observations/${encodeURIComponent(observationId)}/advice`,
    ),
  listCareActions: (observationId: string) =>
    apiGet<{ actions: CareActionRecord[] }>(
      `/api/b/observations/${encodeURIComponent(observationId)}/care-actions`,
    ),
  careAdvice: (body: { observationId: string }) =>
    apiFetch<CareAdviceResult>('/api/b/advice', body),
  careAction: (body: CareActionRequest) =>
    apiFetch<{ actionId: string; performed: boolean }>('/api/b/care-actions', body),
  timeline: (plantId: string) =>
    apiGet<TimelineResult>(`/api/b/timeline?plantId=${encodeURIComponent(plantId)}`),
  listReports: (plantId: string) =>
    apiGet<StageReportListResult>(`/api/b/reports?plantId=${encodeURIComponent(plantId)}`),
  getReport: (reportId: string) =>
    apiGet<StageReportResult>(`/api/b/reports/${encodeURIComponent(reportId)}`),
  stageReport: (body: StageReportRequest) =>
    apiFetch<StageReportResult>('/api/b/report', body),
};
